"use client"

import { useState } from "react";
import { Button, Card, Row, Col, Badge, Modal } from "react-bootstrap";
import * as myApi from "../../components/Api";
import { toast } from "react-toastify";

// Function to convert text to Title Case
const toTitleCase = (str) => {
  if (!str) return str;
  return str.toLowerCase().split(' ').map(word => {
    if (word.length > 0) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    }
    return word;
  }).join(' ');
};

const PaperDetail = ({ paper, onClose, onUpdate }) => {
    const [updating, setUpdating] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmTitle, setConfirmTitle] = useState("");
    const [confirmMessage, setConfirmMessage] = useState("");

    const handleDownload = (url, filename) => {
        let link = document.createElement('a');
        let baseUrl = window.env?.API_URL || process.env.REACT_APP_API_URL || "http://localhost:3001";
        link.href = `${baseUrl}/${url}`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.download = filename || '';
        link.click();
    }

    const getStatusBadge = (status, submitStatus) => {
        if (submitStatus !== 'submit') return <Badge bg="secondary">Not Submitted</Badge>;
        if (status === "lolos") return <Badge bg="success">Approved</Badge>;
        if (status === "gagal") return <Badge bg="danger">Rejected</Badge>;
        return <Badge bg="secondary">Waiting for Approval</Badge>;
    }

    const handleCancelApprove = () => {
        setConfirmAction("cancel_approve");
        setConfirmTitle("Cancel Approval");
        setConfirmMessage(`Are you sure you want to cancel the approval for paper "${paper.title}"? The status will be reset to "Waiting for Approval".`);
        setConfirmOpen(true);
    };

    const handleCancelReject = () => {
        setConfirmAction("cancel_reject");
        setConfirmTitle("Cancel Rejection");
        setConfirmMessage(`Are you sure you want to cancel the rejection for paper "${paper.title}"? The status will be reset to "Waiting for Approval".`);
        setConfirmOpen(true);
    };

    const handleConfirmAction = async () => {
        setUpdating(true);
        try {
            const newStatus = "-";
            const response = await myApi.post("/paper/approval/", {
                paper_code: paper.paper_code,
                paper_status: newStatus
            });

            if (response.data && response.data.status === "success") {
                toast.success("Status successfully reset to 'Waiting for Approval'", {
                    position: "top-right",
                    autoClose: 3000
                });
                if (onUpdate) {
                    onUpdate(response.data.data);
                }
                setConfirmOpen(false);
                if (onClose) onClose();
            } else {
                toast.error(response.data?.message || "Failed to update status", {
                    position: "top-right",
                    autoClose: 5000
                });
            }
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error(error.response?.data?.message || "Error updating status", {
                position: "top-right",
                autoClose: 5000
            });
        } finally {
            setUpdating(false);
        }
    };

    // Debug: Log paper data to console
    console.log('PaperDetail - Paper data:', paper);
    console.log('PaperDetail - name_2:', paper?.name_2);
    console.log('PaperDetail - name_3:', paper?.name_3);

    return (
        <div className="paper-detail-container">
            {/* Header Section */}
            <div className="paper-detail-header mb-4 p-4 bg-light rounded-3">
                <div className="d-flex align-items-start gap-3">
                    <div className="paper-icon-wrapper p-3 bg-primary bg-opacity-10 rounded-3">
                        <i className="material-symbols-outlined fs-1 text-primary">description</i>
                    </div>
                    <div className="flex-grow-1">
                        <h4 className="fw-bold mb-2">{toTitleCase(paper.title)}</h4>
                        <div className="d-flex gap-2 flex-wrap mb-2">
                            {paper.submission_date && paper.submission_date !== '-' && paper.submit_status === 'submit' && (
                                <Badge bg="secondary">{paper.paper_code}</Badge>
                            )}
                            <Badge bg="info">{paper.paper_type}</Badge>
                            {getStatusBadge(paper.paper_status, paper.submit_status)}
                            {paper.submit_status === 'submit' && paper.paper_status === 'lolos' && (
                                <Button variant="outline-warning" size="sm" onClick={handleCancelApprove} disabled={updating}>
                                    <i className="material-symbols-outlined fs-6">close</i>
                                    <span className="ms-1">Cancel Approve</span>
                                </Button>
                            )}
                            {paper.submit_status === 'submit' && paper.paper_status === 'gagal' && (
                                <Button variant="outline-info" size="sm" onClick={handleCancelReject} disabled={updating}>
                                    <i className="material-symbols-outlined fs-6">refresh</i>
                                    <span className="ms-1">Cancel Reject</span>
                                </Button>
                            )}
                        </div>
                        <div className="d-flex gap-3 flex-wrap text-muted small">
                            <span><i className="material-symbols-outlined fs-6 align-middle">calendar_today</i> Submitted: {paper.submit_status === 'submit' && paper.submission_date ? paper.submission_date : '-'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Author Information */}
            <Card className="mb-3 border-0 shadow-sm">
                <Card.Header className="bg-white border-bottom fw-semibold">
                    <i className="material-symbols-outlined fs-5 align-middle me-2">person</i>Author Information
                </Card.Header>
                <Card.Body>
                    {/* Main Author */}
                    <div className="mb-4">
                        <h6 className="fw-bold mb-3 text-primary">
                            <i className="material-symbols-outlined fs-6 align-middle me-1">star</i>
                            Main Author
                        </h6>
                        <Row className="g-3">
                            <Col md={6}>
                                <div className="d-flex align-items-center gap-2">
                                    <i className="material-symbols-outlined fs-6 text-muted">badge</i>
                                    <div>
                                        <small className="text-muted d-block">Full Name</small>
                                        <span className="fw-semibold">{paper.name_1}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="d-flex align-items-center gap-2">
                                    <i className="material-symbols-outlined fs-6 text-muted">email</i>
                                    <div>
                                        <small className="text-muted d-block">Email</small>
                                        <span className="fw-semibold">{paper.email_1}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="d-flex align-items-center gap-2">
                                    <i className="material-symbols-outlined fs-6 text-muted">phone</i>
                                    <div>
                                        <small className="text-muted d-block">Phone</small>
                                        <span className="fw-semibold">{paper.phone_1}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="d-flex align-items-center gap-2">
                                    <i className="material-symbols-outlined fs-6 text-muted">business</i>
                                    <div>
                                        <small className="text-muted d-block">Organization</small>
                                        <span className="fw-semibold">{paper.organization_1 || '-'}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col md={12}>
                                <div className="d-flex align-items-center gap-2">
                                    <i className="material-symbols-outlined fs-6 text-muted">location_on</i>
                                    <div>
                                        <small className="text-muted d-block">Address</small>
                                        <span className="fw-semibold">{paper.address_1 || '-'}</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* Co-Author 2 */}
                    {paper.name_2 && paper.name_2 !== '-' && (
                        <div className="mb-4">
                            <h6 className="fw-bold mb-3 text-info">
                                <i className="material-symbols-outlined fs-6 align-middle me-1">person_add</i>
                                Co-Author 2
                            </h6>
                            <Row className="g-3">
                                <Col md={6}>
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="material-symbols-outlined fs-6 text-muted">badge</i>
                                        <div>
                                            <small className="text-muted d-block">Full Name</small>
                                            <span className="fw-semibold">{paper.name_2}</span>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="material-symbols-outlined fs-6 text-muted">email</i>
                                        <div>
                                            <small className="text-muted d-block">Email</small>
                                            <span className="fw-semibold">{paper.email_2 || '-'}</span>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="material-symbols-outlined fs-6 text-muted">phone</i>
                                        <div>
                                            <small className="text-muted d-block">Phone</small>
                                            <span className="fw-semibold">{paper.phone_2 || '-'}</span>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="material-symbols-outlined fs-6 text-muted">business</i>
                                        <div>
                                            <small className="text-muted d-block">Organization</small>
                                            <span className="fw-semibold">{paper.organization_2 || '-'}</span>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={12}>
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="material-symbols-outlined fs-6 text-muted">location_on</i>
                                        <div>
                                            <small className="text-muted d-block">Address</small>
                                            <span className="fw-semibold">{paper.address_2 || '-'}</span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {/* Co-Author 3 */}
                    {paper.name_3 && paper.name_3 !== '-' && (
                        <div>
                            <h6 className="fw-bold mb-3 text-info">
                                <i className="material-symbols-outlined fs-6 align-middle me-1">person_add</i>
                                Co-Author 3
                            </h6>
                            <Row className="g-3">
                                <Col md={6}>
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="material-symbols-outlined fs-6 text-muted">badge</i>
                                        <div>
                                            <small className="text-muted d-block">Full Name</small>
                                            <span className="fw-semibold">{paper.name_3}</span>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="material-symbols-outlined fs-6 text-muted">email</i>
                                        <div>
                                            <small className="text-muted d-block">Email</small>
                                            <span className="fw-semibold">{paper.email_3 || '-'}</span>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="material-symbols-outlined fs-6 text-muted">phone</i>
                                        <div>
                                            <small className="text-muted d-block">Phone</small>
                                            <span className="fw-semibold">{paper.phone_3 || '-'}</span>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="material-symbols-outlined fs-6 text-muted">business</i>
                                        <div>
                                            <small className="text-muted d-block">Organization</small>
                                            <span className="fw-semibold">{paper.organization_3 || '-'}</span>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={12}>
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="material-symbols-outlined fs-6 text-muted">location_on</i>
                                        <div>
                                            <small className="text-muted d-block">Address</small>
                                            <span className="fw-semibold">{paper.address_3 || '-'}</span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Paper Details */}
            <Card className="mb-3 border-0 shadow-sm">
                <Card.Header className="bg-white border-bottom fw-semibold">
                    <i className="material-symbols-outlined fs-5 align-middle me-2">topic</i>Paper Details
                </Card.Header>
                <Card.Body>
                    <Row className="g-3">
                        <Col md={12}>
                            <div className="d-flex align-items-center gap-2">
                                <i className="material-symbols-outlined fs-6 text-muted">topic</i>
                                <div>
                                    <small className="text-muted d-block">Sub Theme</small>
                                    <Badge bg="success">{paper.sub_theme}</Badge>
                                </div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex align-items-center gap-2">
                                <i className="material-symbols-outlined fs-6 text-muted">category</i>
                                <div>
                                    <small className="text-muted d-block">Category</small>
                                    <span className="fw-medium">{paper.category}</span>
                                </div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex align-items-center gap-2">
                                <i className="material-symbols-outlined fs-6 text-muted">groups</i>
                                <div>
                                    <small className="text-muted d-block">Participation Type</small>
                                    <Badge bg="secondary">{paper.participation_type}</Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Download Files */}
            <Card className="mb-3 border-0 shadow-sm">
                <Card.Header className="bg-white border-bottom fw-semibold">
                    <i className="material-symbols-outlined fs-5 align-middle me-2">download</i>Download Files
                </Card.Header>
                <Card.Body>
                    <Row className="g-3">
                        {/* Main Author Files */}
                        <Col md={6}>
                            <div className="file-item p-3 bg-light rounded-3">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <i className="material-symbols-outlined fs-5 text-primary">description</i>
                                    <div className="fw-semibold">Main Author Files</div>
                                </div>
                                <div className="d-grid gap-2">
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="justify-content-start"
                                        onClick={() => handleDownload(paper.paper_filePath_1, paper.paper_fileName_1)}
                                    >
                                        <i className="material-symbols-outlined fs-6">download</i>
                                        <span className="ms-2">Paper Document</span>
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="justify-content-start"
                                        onClick={() => handleDownload(paper.pernyataan_filePath_1, paper.pernyataan_fileName_1)}
                                    >
                                        <i className="material-symbols-outlined fs-6">file_present</i>
                                        <span className="ms-2">Statement Letter</span>
                                    </Button>
                                    <Button
                                        variant="outline-warning"
                                        size="sm"
                                        className="justify-content-start"
                                        onClick={() => handleDownload(paper.anonymous_manuscript_filePath, paper.anonymous_manuscript_fileName)}
                                    >
                                        <i className="material-symbols-outlined fs-6">visibility_off</i>
                                        <span className="ms-2">Anonymous Manuscript</span>
                                    </Button>
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        className="justify-content-start"
                                        onClick={() => handleDownload(paper.cv_filePath_1, paper.cv_fileName_1)}
                                    >
                                        <i className="material-symbols-outlined fs-6">account_circle</i>
                                        <span className="ms-2">Author CV</span>
                                    </Button>
                                    {/* KTP file for Umum category */}
                                    {paper.category === "Umum" && paper.ktp_filePath_1 && paper.ktp_filePath_1 !== '-' && (
                                        <Button
                                            variant="outline-dark"
                                            size="sm"
                                            className="justify-content-start"
                                            onClick={() => handleDownload(paper.ktp_filePath_1, paper.ktp_fileName_1)}
                                        >
                                            <i className="material-symbols-outlined fs-6">badge</i>
                                            <span className="ms-2">KTP (Author 1)</span>
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Col>

                        {/* Additional Files - Only show for Mahasiswa category */}
                        {paper.category === "Mahasiswa" && (
                            <Col md={6}>
                                <div className="file-item p-3 bg-light rounded-3">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <i className="material-symbols-outlined fs-5 text-success">school</i>
                                        <div className="fw-semibold">Academic Files</div>
                                    </div>
                                    <div className="d-grid gap-2">
                                        {paper.pddikti_filePath_1 && paper.pddikti_filePath_1 !== '-' && (
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="justify-content-start"
                                                onClick={() => handleDownload(paper.pddikti_filePath_1, paper.pddikti_fileName_1)}
                                            >
                                                <i className="material-symbols-outlined fs-6">verified</i>
                                                <span className="ms-2">PDDIKTI Evidence (Main)</span>
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        )}

                        {/* Co-Author 2 Files */}
                        {paper.name_2 && paper.name_2 !== '-' && (
                            <Col md={6}>
                                <Card className="border-0 bg-light rounded-3">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center gap-2 mb-3">
                                            <i className="material-symbols-outlined fs-5 text-info">person_add</i>
                                            <div>
                                                <div className="fw-semibold">Co-Author 2</div>
                                                <small className="text-muted">{paper.name_2}</small>
                                            </div>
                                        </div>
                                        <div className="text-start mb-3">
                                            <div className="mb-2">
                                                <small className="text-muted">Email:</small>
                                                <div className="ms-2">{paper.email_2 && paper.email_2 !== '-' ? paper.email_2 : '-'}</div>
                                            </div>
                                            <div className="mb-2">
                                                <small className="text-muted">Instansi:</small>
                                                <div className="ms-2">{paper.organization_2 && paper.organization_2 !== '-' ? paper.organization_2 : '-'}</div>
                                            </div>
                                            <div className="mb-2">
                                                <small className="text-muted">Handphone:</small>
                                                <div className="ms-2">{paper.phone_2 && paper.phone_2 !== '-' ? paper.phone_2 : '-'}</div>
                                            </div>
                                            <div className="mb-2">
                                                <small className="text-muted">Alamat Domisili:</small>
                                                <div className="ms-2">{paper.address_2 && paper.address_2 !== '-' ? paper.address_2 : '-'}</div>
                                            </div>
                                        </div>
                                        <div className="d-grid gap-2">
                                            {paper.cv_filePath_2 && (
                                                <Button
                                                    variant="outline-info"
                                                    size="sm"
                                                    className="justify-content-start"
                                                    onClick={() => handleDownload(paper.cv_filePath_2, paper.cv_fileName_2)}
                                                >
                                                    <i className="material-symbols-outlined fs-6">account_circle</i>
                                                    <span className="ms-2">CV</span>
                                                </Button>
                                            )}
                                            {paper.category === "Mahasiswa" && paper.pddikti_filePath_2 && (
                                                <Button
                                                    variant="outline-success"
                                                    size="sm"
                                                    className="justify-content-start"
                                                    onClick={() => handleDownload(paper.pddikti_filePath_2, paper.pddikti_fileName_2)}
                                                >
                                                    <i className="material-symbols-outlined fs-6">verified</i>
                                                    <span className="ms-2">PDDIKTI Evidence</span>
                                                </Button>
                                            )}
                                            {paper.category === "Umum" && paper.ktp_filePath_2 && paper.ktp_filePath_2 !== '-' && (
                                                <Button
                                                    variant="outline-dark"
                                                    size="sm"
                                                    className="justify-content-start"
                                                    onClick={() => handleDownload(paper.ktp_filePath_2, paper.ktp_fileName_2)}
                                                >
                                                    <i className="material-symbols-outlined fs-6">badge</i>
                                                    <span className="ms-2">KTP</span>
                                                </Button>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}

                        {/* Co-Author 3 Files */}
                        {paper.name_3 && paper.name_3 !== '-' && (
                            <Col md={6}>
                                <Card className="border-0 bg-light rounded-3">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center gap-2 mb-3">
                                            <i className="material-symbols-outlined fs-5 text-info">person_add</i>
                                            <div>
                                                <div className="fw-semibold">Co-Author 3</div>
                                                <small className="text-muted">{paper.name_3}</small>
                                            </div>
                                        </div>
                                        <div className="text-start mb-3">
                                            <div className="mb-2">
                                                <small className="text-muted">Email:</small>
                                                <div className="ms-2">{paper.email_3 && paper.email_3 !== '-' ? paper.email_3 : '-'}</div>
                                            </div>
                                            <div className="mb-2">
                                                <small className="text-muted">Instansi:</small>
                                                <div className="ms-2">{paper.organization_3 && paper.organization_3 !== '-' ? paper.organization_3 : '-'}</div>
                                            </div>
                                            <div className="mb-2">
                                                <small className="text-muted">Handphone:</small>
                                                <div className="ms-2">{paper.phone_3 && paper.phone_3 !== '-' ? paper.phone_3 : '-'}</div>
                                            </div>
                                            <div className="mb-2">
                                                <small className="text-muted">Alamat Domisili:</small>
                                                <div className="ms-2">{paper.address_3 && paper.address_3 !== '-' ? paper.address_3 : '-'}</div>
                                            </div>
                                        </div>
                                        <div className="d-grid gap-2">
                                            {paper.cv_filePath_3 && (
                                                <Button
                                                    variant="outline-info"
                                                    size="sm"
                                                    className="justify-content-start"
                                                    onClick={() => handleDownload(paper.cv_filePath_3, paper.cv_fileName_3)}
                                                >
                                                    <i className="material-symbols-outlined fs-6">account_circle</i>
                                                    <span className="ms-2">CV</span>
                                                </Button>
                                            )}
                                            {paper.category === "Mahasiswa" && paper.pddikti_filePath_3 && (
                                                <Button
                                                    variant="outline-success"
                                                    size="sm"
                                                    className="justify-content-start"
                                                    onClick={() => handleDownload(paper.pddikti_filePath_3, paper.pddikti_fileName_3)}
                                                >
                                                    <i className="material-symbols-outlined fs-6">verified</i>
                                                    <span className="ms-2">PDDIKTI Evidence</span>
                                                </Button>
                                            )}
                                            {paper.category === "Umum" && paper.ktp_filePath_3 && paper.ktp_filePath_3 !== '-' && (
                                                <Button
                                                    variant="outline-dark"
                                                    size="sm"
                                                    className="justify-content-start"
                                                    onClick={() => handleDownload(paper.ktp_filePath_3, paper.ktp_fileName_3)}
                                                >
                                                    <i className="material-symbols-outlined fs-6">badge</i>
                                                    <span className="ms-2">KTP</span>
                                                </Button>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </Row>
                </Card.Body>
            </Card>

            {/* Confirmation Modal */}
            <Modal show={confirmOpen} onHide={() => setConfirmOpen(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{confirmTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{confirmMessage}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setConfirmOpen(false)} disabled={updating}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleConfirmAction} disabled={updating}>
                        {updating ? "Processing..." : "Confirm"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default PaperDetail